import { useState } from "react"
import { CheckCircle2, Flag, Loader2, Printer } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { formatDateOnly, formatTimestamp } from "@/lib/datetime"
import { cn } from "@/lib/utils"
import { tcmErrorMessage } from "../../api"
import { useHandoutPdf, useTcmAction } from "../../hooks"
import { blankLabel, humanize, LANG_LABEL } from "../../lib/careplan"
import type { TcmCarePlan, TcmEpisodeDetail, TcmLang } from "../../types"

const LANGS: TcmLang[] = ["en", "es", "vi"]
const CHANGE: Record<string, string> = { new: "New", changed: "Changed", stopped: "Stopped", held: "On hold" }

/**
 * "My next 4 weeks": the patient's printed plan, previewed exactly as it prints (the server renders
 * it), in each approved language. Blanks the clinic hasn't filled print as write-in lines.
 */
export function PatientPlanPanel({ ep, plan, today }: { ep: TcmEpisodeDetail; plan: TcmCarePlan; today: string }) {
  const printable = plan.languages
  const [lang, setLang] = useState<TcmLang | null>(printable.includes(plan.default_language) ? plan.default_language : printable[0] ?? null)
  const [showBlanks, setShowBlanks] = useState(true)
  const [dialog, setDialog] = useState<"given" | "flag" | null>(null)
  const stamp = `${plan.version}:${plan.bridges.map((b) => b.updated_at).join(",")}`
  const pdf = useHandoutPdf(ep.id, lang, showBlanks, stamp)
  const given = ep.events.filter((e) => e.step === "education" && e.state === "handout_given")

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 pb-3">
          <div className="flex rounded-lg border p-0.5" role="group" aria-label="Language">
            {LANGS.map((l) => (
              <button
                key={l}
                type="button"
                disabled={!printable.includes(l)}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                title={printable.includes(l) ? undefined : "This language's text isn't approved yet"}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-medium",
                  lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                  "disabled:cursor-not-allowed disabled:opacity-40",
                )}
              >
                {LANG_LABEL[l]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="checkbox" checked={showBlanks} onChange={(e) => setShowBlanks(e.target.checked)} />
              Highlight blanks
            </label>
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={!pdf.url || !lang}
              onClick={() => window.open(pdf.url!, "_blank", "noopener")}
            >
              <Printer className="h-3.5 w-3.5" />
              Open to print
            </Button>
            <Button size="sm" className="h-8 gap-1.5 text-xs" disabled={!lang} onClick={() => setDialog("given")}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Mark given
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!lang ? (
            <div className="px-6 py-16 text-center text-sm text-muted-foreground">
              The plan's wording is still in Pear's clinical review, so it can't print yet. Everything else on this page
              is ready to use.
            </div>
          ) : pdf.isLoading ? (
            <div className="flex h-[70vh] items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : pdf.error || !pdf.url ? (
            <div className="px-6 py-16 text-center text-sm text-destructive">
              Couldn't load the plan: {tcmErrorMessage(pdf.error)}
            </div>
          ) : (
            <iframe title="The patient's 4-week plan" src={pdf.url} className="h-[75vh] w-full border-0 bg-muted" />
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Given to the patient</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-[13px]">
            {given.length ? (
              given.map((e) => (
                <p key={e.at}>
                  {/* "plan v2 es on Tue 10/20; pdf sha1:…" -> "Plan v2 es on Tue 10/20" */}
                  {humanize(e.detail?.split(";")[0] ?? "Given")}
                  <span className="block text-[11px] text-muted-foreground">
                    Recorded by {e.actor} · {formatTimestamp(e.at, "EEE M/d")}
                  </span>
                </p>
              ))
            ) : (
              <p className="text-muted-foreground">Not yet. Print it at the visit and mark it given.</p>
            )}
          </CardContent>
        </Card>

        {plan.blanks.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Prints as write-in lines</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc space-y-1 pl-4 text-[13px] text-muted-foreground">
                {plan.blanks.map((b) => (
                  <li key={b}>{blankLabel(b)}</li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Fill them under Needs at home or in the resource directory, or write them in by hand.
              </p>
            </CardContent>
          </Card>
        )}

        <PlanContents plan={plan} />

        <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs text-muted-foreground" onClick={() => setDialog("flag")}>
          <Flag className="h-3.5 w-3.5" />
          Something wrong in the plan?
        </Button>
      </div>

      {dialog && (
        <PlanDialog ep={ep} mode={dialog} lang={lang ?? "en"} today={today} onClose={() => setDialog(null)} />
      )}
    </div>
  )
}

function PlanContents({ plan }: { plan: TcmCarePlan }) {
  const pp = plan.patient_plan
  const meds = pp.medications ?? []
  const visits = pp.appointments ?? []
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">What the plan covers</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-[13px]">
        {!!pp.conditions?.length && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Conditions</p>
            <p>{pp.conditions.map((c) => humanize(c.split(".").pop() ?? c)).join(", ")}</p>
          </div>
        )}
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Medicine changes from the hospital</p>
          {meds.length ? (
            <ul className="space-y-0.5">
              {meds.map((m) => (
                <li key={`${m.change}:${m.name}`}>
                  <span className="text-muted-foreground">{CHANGE[m.change] ?? m.change}:</span> {m.name}
                  {m.strength ? ` ${m.strength}` : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground">None in the records.</p>
          )}
        </div>
        {visits.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Visits and tests</p>
            <ul className="space-y-0.5">
              {visits.map((a, i) => (
                <li key={i}>
                  {a.kind === "tcm_visit" ? "Follow-up visit" : a.kind === "lab" ? "Labs" : a.specialty ?? "Specialist"}
                  {a.date ? ` · ${formatDateOnly(a.date, "EEE M/d")}` : a.by_date ? ` · by ${formatDateOnly(a.by_date, "EEE M/d")}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">
          {pp.warning_signs?.length ?? 0} warning signs · {pp.self_checks?.length ?? 0} daily checks ·{" "}
          {pp.pending_results ?? 0} pending results · plan v{plan.version}
        </p>
      </CardContent>
    </Card>
  )
}

function PlanDialog({
  ep,
  mode,
  lang,
  today,
  onClose,
}: {
  ep: TcmEpisodeDetail
  mode: "given" | "flag"
  lang: TcmLang
  today: string
  onClose: () => void
}) {
  const { toast } = useToast()
  const action = useTcmAction(ep.id)
  const [date, setDate] = useState(today)
  const [text, setText] = useState("")
  const save = () =>
    action.mutate(
      mode === "given"
        ? { action: "handout_given", date, lang }
        : { action: "flag_issue", target: "plan", note: text.trim() },
      {
        onSuccess: () => {
          toast({ title: mode === "given" ? "Marked given. Pear kept a copy of the exact plan printed." : "Sent to Pear" })
          onClose()
        },
        onError: (err) => toast({ title: "Not saved", description: tcmErrorMessage(err), variant: "destructive" }),
      },
    )
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{mode === "given" ? "Plan given to the patient" : "Something wrong in the plan"}</DialogTitle>
          <DialogDescription>
            {mode === "given"
              ? `In ${LANG_LABEL[lang]}. This is the record of the patient education for the TCM claim.`
              : "Tell Pear what's wrong. A clinician reviews it and fixes the plan."}
          </DialogDescription>
        </DialogHeader>
        {mode === "given" ? (
          <div className="grid gap-1.5">
            <Label htmlFor="given-date">Date given</Label>
            <Input id="given-date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} />
          </div>
        ) : (
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="The water pill was stopped at the visit…" />
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={action.isPending || (mode === "flag" && !text.trim())}>
            {action.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {mode === "given" ? "Mark given" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
